// The UI thread's side of the data worker: an async API over host.js, plus
// the sandbox it needs (an iframe can only be created here). Table rows stay
// in the worker; the UI gets summaries and pages of rows.
import { startSandbox } from "../../../engine/code/sandbox";

let worker = null;
let nextId = 1;
const pending = new Map(); // id -> { resolve, reject }
const sandboxes = new Map(); // sandbox id -> { stop }, one per running job

// Starts a sandbox for one run and hands its port to the data worker.
const connectSandbox = async (sandboxId) => {
  try {
    const sandbox = await startSandbox();
    if (!worker) return sandbox.stop(); // the worker crashed meanwhile
    sandboxes.set(sandboxId, sandbox);
    return worker.postMessage({ type: "sandbox:port", sandboxId, port: sandbox.port }, [sandbox.port]);
  } catch (error) {
    return worker?.postMessage({ type: "sandbox:failed", sandboxId, message: error.message });
  }
};

// Removes a sandbox's iframe, which kills its worker and frees its memory.
const releaseSandbox = (sandboxId) => {
  sandboxes.get(sandboxId)?.stop();
  sandboxes.delete(sandboxId);
};

const onMessage = ({ data }) => {
  if (data.type === "sandbox:request") return connectSandbox(data.sandboxId);
  if (data.type === "sandbox:release") return releaseSandbox(data.sandboxId);
  const call = pending.get(data.id);
  if (!call) return undefined;
  pending.delete(data.id);
  if (data.ok) call.resolve(data.result);
  else call.reject(Object.assign(new Error(data.error.message), { name: data.error.name, timeout: data.error.timeout }));
  return undefined;
};

// The worker can die (e.g. out of memory on a huge file); loaded files are
// lost with it, so callers see an error and the next call starts a new one.
const onCrash = () => {
  const error = new Error("The data engine stopped, probably because the file is too large. Reload the page and upload the file again.");
  pending.forEach((call) => call.reject(error));
  pending.clear();
  worker?.terminate();
  worker = null;
  [...sandboxes.keys()].forEach(releaseSandbox);
};

const getWorker = () => {
  if (!worker) {
    worker = new Worker(new URL("./data.worker.js", import.meta.url));
    worker.onmessage = onMessage;
    worker.onerror = onCrash;
  }
  return worker;
};

const call = (method, ...args) =>
  new Promise((resolve, reject) => {
    const id = nextId++;
    pending.set(id, { resolve, reject });
    getWorker().postMessage({ id, method, args });
  });

// See host.js for what each method does. A file ref is
// { key, sheetName, columnOverrides? }; a table ref is { id } or { output }.
export const dataHost = {
  loadFile: (file) => call("loadFile", file),
  releaseFiles: (keys) => call("releaseFiles", keys),
  describeSource: (fileRef, settings) => call("describeSource", fileRef, settings),
  checkSource: (source, fileRef) => call("checkSource", source, fileRef),
  dataWords: (keys, words) => call("dataWords", keys, words),
  columnProfiles: (workflow, fileRefs) => call("columnProfiles", workflow, fileRefs),
  valueColumns: (workflow, fileRefs, values) => call("valueColumns", workflow, fileRefs, values),
  preview: (scope, workflow, fileRefs) => call("preview", scope, workflow, fileRefs),
  runFull: (workflow, fileRefs) => call("runFull", workflow, fileRefs),
  dropScope: (scope) => call("dropScope", scope),
  describeTable: (scope, ref) => call("describeTable", scope, ref),
  getProfile: (scope, ref, options) => call("getProfile", scope, ref, options),
  createView: (scope, ref, options) => call("createView", scope, ref, options),
  dropView: (viewId) => call("dropView", viewId),
  getDiff: (scope, ref, inputId) => call("getDiff", scope, ref, inputId),
  getRows: (scope, ref, options) => call("getRows", scope, ref, options),
  getLeadingRows: (scope, ref) => call("getLeadingRows", scope, ref),
  positionOf: (viewId, index) => call("positionOf", viewId, index),
  buildOutput: (scope, output) => call("buildOutput", scope, output),
};

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Builds an output file in the worker from a scope's results and saves it.
export const downloadOutput = async (scope, output) => {
  const file = await dataHost.buildOutput(scope, output);
  saveBlob(new Blob([file.buffer ?? file.text], { type: file.type }), file.fileName);
};
